import { Button, DatePicker, Input, Select, useToast } from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import {
  ApiClientError,
  type DeviceOs,
  type DeviceRow,
  type UpdateDeviceBody,
} from '@uchat-team-management/shared';
import { Show, createSignal } from 'solid-js';
import { useUpdateDevice } from './hooks';

interface Props {
  device: DeviceRow;
}

export const DeviceForm = (props: Props) => {
  const update = useUpdateDevice(props.device.id);
  const toast = useToast();
  const { session } = useAuth();
  const [name, setName] = createSignal(props.device.name);
  const [model, setModel] = createSignal(props.device.model);
  const [os, setOs] = createSignal<DeviceOs>(props.device.os);
  const [serial, setSerial] = createSignal(props.device.serial);
  const [imei, setImei] = createSignal(props.device.imei);
  const [status, setStatus] = createSignal(props.device.status);
  const [notes, setNotes] = createSignal(props.device.notes);
  const [purchaseDate, setPurchaseDate] = createSignal(props.device.purchaseDate ?? '');
  const [warrantyExpiresAt, setWarrantyExpiresAt] = createSignal(
    props.device.warrantyExpiresAt ?? '',
  );
  const [error, setError] = createSignal<string | null>(null);

  const canEdit = () => session()?.permissions.has('devices:edit') ?? false;
  // Members with only editOwnNote (and holding the device) get a notes-only form.
  const notesOnly = () =>
    !canEdit() &&
    (session()?.permissions.has('devices:editOwnNote') ?? false) &&
    props.device.assignedUserId === session()?.id;
  // `assigned` is API-maintained; the dropdown only offers manual statuses.
  const statusLocked = () => props.device.status === 'assigned';

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    setError(null);
    if (notesOnly()) {
      try {
        await update.mutateAsync({ notes: notes() });
        toast.success('Note updated');
      } catch (err) {
        setError(err instanceof ApiClientError ? err.error.message : 'Update failed');
      }
      return;
    }
    const inventory = {
      purchaseDate: purchaseDate() || null,
      warrantyExpiresAt: warrantyExpiresAt() || null,
    };
    const body: UpdateDeviceBody = {
      name: name(),
      model: model(),
      os: os(),
      serial: serial(),
      imei: imei(),
      notes: notes(),
      ...inventory,
    };
    const nextStatus = status();
    if (!statusLocked() && nextStatus !== 'assigned') body.status = nextStatus;
    try {
      await update.mutateAsync(body);
      toast.success('Device updated');
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.error.message);
      } else {
        setError((err as Error).message ?? 'Update failed');
      }
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      class="flex flex-col gap-4 rounded-lg border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark"
    >
      <Show
        when={!notesOnly()}
        fallback={
          <>
            <h2 class="text-base font-semibold">Notes</h2>
            <Input label="Notes" value={notes()} onInput={(e) => setNotes(e.currentTarget.value)} />
          </>
        }
      >
        <h2 class="text-base font-semibold">Identity</h2>
        <Input
          label="Name"
          value={name()}
          onInput={(e) => setName(e.currentTarget.value)}
          required
        />
        <Input label="Model" value={model()} onInput={(e) => setModel(e.currentTarget.value)} />
        <Select
          label="OS"
          value={os()}
          onChange={(v) => setOs(v as DeviceOs)}
          options={[
            { value: 'ios', label: 'iOS' },
            { value: 'android', label: 'Android' },
            { value: 'other', label: 'Other' },
          ]}
        />
        <Input label="Serial" value={serial()} onInput={(e) => setSerial(e.currentTarget.value)} />
        <Input label="IMEI" value={imei()} onInput={(e) => setImei(e.currentTarget.value)} />
        <Select
          label="Status"
          value={status()}
          onChange={(v) => setStatus(v as DeviceRow['status'])}
          disabled={statusLocked()}
          hint={statusLocked() ? 'Managed by assignment — unassign to change.' : undefined}
          options={
            statusLocked()
              ? [{ value: 'assigned', label: 'Assigned' }]
              : [
                  { value: 'available', label: 'Available' },
                  { value: 'lost', label: 'Lost' },
                  { value: 'retired', label: 'Retired' },
                ]
          }
        />
        <Input label="Notes" value={notes()} onInput={(e) => setNotes(e.currentTarget.value)} />

        <h2 class="text-base font-semibold">Inventory</h2>
        <DatePicker label="Purchase date" value={purchaseDate()} onChange={setPurchaseDate} />
        <DatePicker
          label="Warranty expires"
          value={warrantyExpiresAt()}
          onChange={setWarrantyExpiresAt}
        />
      </Show>
      <Show when={error()}>
        <p class="text-xs text-red-600">{error()}</p>
      </Show>
      <div class="flex justify-end">
        <Button type="submit" loading={update.isPending}>
          Save
        </Button>
      </div>
    </form>
  );
};
